// Filename: src/components/ImageList.js
//----------------------------------------
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { apiConfig } from '../config/apiConfig'; // Import API configuration
import Analytics from '../services/analytics'; // Import Analytics service
import { createSessionHeaders } from '../utils/sessionManager'; // Import session manager
import './ImageList.css';
import { TransitionGroup, CSSTransition } from 'react-transition-group';
import { isImageBlack } from '../utils/imageQuality';

const featuresUrl = apiConfig.getDates;
const imageUrl = apiConfig.getS2Image;

const monthNames = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

const BATCH_SIZE = 100;

const ImageCell = ({ image, onImageClick, showClouds, boxDimensions, imageViewMode, isSelected }) => {
  const [showDate, setShowDate] = useState(false);

  useEffect(() => {
    let timer;
    if (image.loading) {
      timer = setTimeout(() => {
        setShowDate(true);
      }, 1000); // Show date after 1 second
    }
    return () => {
      clearTimeout(timer);
    };
  }, [image.loading]);

  const handleClick = () => {
    if (image.loading || !image.url) return;
    
    // Track image selection
    Analytics.trackImageSelected(
      image.date,
      image.percentages?.cloud_percentage,
      `${image.latitude},${image.longitude}`,
      'thumbnail_click'
    );
    
    onImageClick(image);
  };

  // Calculate cell dimensions based on box aspect ratio
  const getCellStyle = () => {
    if (!boxDimensions) {
      return {
        width: '100px',
        height: '100px'
      };
    }

    // Scale factor to convert from meters to pixels
    // We'll use 100px for the max dimension
    const scaleFactor = 100 / Math.max(boxDimensions.width, boxDimensions.height);

    // Calculate dimensions capped at twice the size or 100px, whichever is less
    let width = Math.min(100, boxDimensions.width * scaleFactor * 2);
    let height = Math.min(100, boxDimensions.height * scaleFactor * 2);

    // Ensure we maintain the aspect ratio exactly
    const aspectRatio = boxDimensions.width / boxDimensions.height;

    if (aspectRatio >= 1) {
      // Width is the larger dimension, so set it first and derive height
      height = width / aspectRatio;
    } else {
      // Height is the larger dimension, so set it first and derive width
      width = height * aspectRatio;
    }

    return {
      width: `${Math.round(width)}px`,
      height: `${Math.round(height)}px`
    }
  };

  return (
    <div
      className={`image-cell ${
        image.loading || !image.url ? 'disabled' : ''
      } ${image.superresolved ? 'superresolved' : ''} ${isSelected ? 'selected' : ''}`}
      onClick={handleClick}
      style={getCellStyle()}
    >
      {image.loading ? (
        <div className="loading-indicator">
          <div className="spinner"></div>
          {showDate && (
            <span className="image-date">
              {new Date(image.date).getDate()}
            </span>
          )}
        </div>
      ) : image.url ? (
        <>
          <img
            src={
              imageViewMode === 'evi' && image.evi_url 
                ? image.evi_url 
                : imageViewMode === 'false_color' && image.false_color_url 
                ? image.false_color_url 
                : image.url
            }
            alt={`Sentinel-2 scene from ${new Date(
              image.date
            ).toLocaleDateString()}`}
            className="satellite-image"
          />
          <span className="image-date">{new Date(image.date).getDate()}</span>
          {image.superresolved && <span className="magic-label">Magic</span>}
          {showClouds && image.percentages && (
            <span className="cloud-percentage">
              Cloud: {image.percentages.cloud_percentage.toFixed(1)}%
            </span>
          )}
        </>
      ) : (
        <div className="image-placeholder">No image</div>
      )}
    </div>
  );
};

const ImageList = ({
  bbox,
  onImageClick,
  updateImages,
  images,
  allImages = [],
  selectedImagePaths = new Set(),
  boxDimensions,
  imageViewMode,
  onToggleImageView,
  loadingBatch: externalLoadingBatch = false,
  useGlobalImageLoader = false,
}) => {
  const [year, setYear] = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(false);
  const [loadingBatch, setLoadingBatch] = useState(false);
  const [fetchedYears, setFetchedYears] = useState(new Set());
  const [forceRefresh] = useState(0); // Add force refresh counter
  const isMountedRef = useRef(true);

  const showClouds = true;
  const effectiveLoadingBatch = useGlobalImageLoader ? externalLoadingBatch : loadingBatch;

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const fetchImagesForYear = useCallback(
    async (fetchYear, bboxParam = bbox) => {
      if (fetchedYears.has(fetchYear)) {
        console.log(`Data for year ${fetchYear} already fetched, skipping.`);
        return;
      }
      if (allImages.length > 0) {
        const hasCachedYear = allImages.some((img) => new Date(img.date).getFullYear() === fetchYear);
        if (hasCachedYear) {
          console.log(`Data for year ${fetchYear} already cached, skipping refetch.`);
          if (isMountedRef.current) {
            setFetchedYears((prevYears) => new Set(prevYears).add(fetchYear));
          }
          return;
        }
      }

      if (!bboxParam || bboxParam.length !== 4) {
        console.error('Invalid bbox:', bboxParam);
        return;
      }

      if (isMountedRef.current) {
        setLoading(true);
      }
      try {
        const bboxString = bboxParam.join(',');
        
        // Debug: Log cookies
        console.log('🍪 ImageList - Document cookies:', document.cookie);
        console.log('🍪 ImageList - Cookie check:', {
          has_ga: document.cookie.includes('_ga='),
          has_gid: document.cookie.includes('_gid='),
          cookie_length: document.cookie.length
        });
        
        const response = await fetch(
          `${featuresUrl}?bbox=${bboxString}&year=${fetchYear}`,
          { 
            credentials: 'include',
            headers: {
              ...createSessionHeaders(),
              'X-GA-Cookies': document.cookie // Send cookies in custom header
            }
          }
        );
        const data = await response.json();
        console.log('Fetched images data:', data);

        // Check if data is an array before processing
        if (!Array.isArray(data)) {
          console.error('Unexpected response format from getDates API:', data);
          throw new Error('Invalid response format');
        }

        // Track images requested
        const center = [(bboxParam[0] + bboxParam[2]) / 2, (bboxParam[1] + bboxParam[3]) / 2];
        Analytics.trackImagesRequested(bboxParam, fetchYear, `${center[1]},${center[0]}`);

        const newImages = data.map((img) => ({ ...img, loading: true }));
        updateImages(newImages);
        if (isMountedRef.current) {
          setFetchedYears((prevYears) => new Set(prevYears).add(fetchYear));
        }
        
        // Calculate cloud distribution
        const cloudPercentages = data
          .map(img => img.cloud_percentage)
          .filter(val => val !== undefined && val !== null);
        
        if (cloudPercentages.length > 0) {
          const cloudDistribution = {
            avg: cloudPercentages.reduce((a, b) => a + b, 0) / cloudPercentages.length,
            min: Math.min(...cloudPercentages),
            max: Math.max(...cloudPercentages)
          };
          
          // Track images loaded
          Analytics.trackImagesLoaded(data.length, cloudDistribution, fetchYear);
        }

      } catch (error) {
        console.error('Error fetching images:', error);
        // Track API error
        Analytics.trackApiError('getDates', error.message || 'fetch_failed', `year: ${fetchYear}`);
      } finally {
        if (isMountedRef.current) {
          setLoading(false);
        }
      }
    },
    [bbox, fetchedYears, updateImages, allImages]
  );

  useEffect(() => {
    if (bbox && bbox.length === 4 && !fetchedYears.has(year)) {
      fetchImagesForYear(year, bbox);
    }
  }, [bbox, fetchImagesForYear, year, fetchedYears, forceRefresh]);

  useEffect(() => {
    const currentYear = new Date().getFullYear();
    const previousYear = currentYear - 1;
    if (bbox && bbox.length === 4 && fetchedYears.has(currentYear) && !fetchedYears.has(previousYear)) {
      fetchImagesForYear(previousYear, bbox);
    }
  }, [bbox, fetchImagesForYear, fetchedYears]);

  const fetchImageBatch = useCallback(
    async (imagesToFetch) => {
      setLoadingBatch(true);
      const fetchPromises = imagesToFetch.map(async (image) => {
        try {
          const bboxString = bbox.join(',');
          let apiUrl = `${imageUrl}?bbox=${bboxString}&s3_path=${image.path}`;
          if (showClouds) {
            apiUrl += '&calcClouds=true';
          }
          const startTime = Date.now();
          const response = await fetch(apiUrl, { 
            credentials: 'include',
            headers: {
              ...createSessionHeaders(),
              'X-GA-Cookies': document.cookie // Send cookies in custom header
            }
          });
          const loadTime = Date.now() - startTime;
          
          // Track slow loads
          if (loadTime > 3000) {
            Analytics.trackSlowLoad('satellite_image', loadTime, apiUrl);
          }
          
          const data = await response.json();
          console.log('API response data:', {
            is_black: data.is_black,
            has_false_color_link: 'false_color_link' in data,
            false_color_link: data.false_color_link,
            has_evi_link: 'evi_link' in data,
            evi_link: data.evi_link,
            full_response: data,
            api_url: apiUrl
          });
          return {
            ...image,
            url: data.image_link,
            false_color_url: data.false_color_link,
            false_color_s3_path: data.false_color_s3_path,
            evi_url: data.evi_link,
            evi_s3_path: data.evi_s3_path,
            percentages: data.percentages,
            stacked_tif_s3_path: data.stacked_tif_s3_path,
            is_black: data.is_black,
            black_percentage: data.black_percentage,
            loading: false,
          };
        } catch (error) {
          console.error('Error fetching image:', error);
          // Track API error
          Analytics.trackApiError('getS2Image', error.message || 'fetch_failed', `path: ${image.path}`);
          return { ...image, loading: false };
        }
      });

      const fetchedImages = await Promise.all(fetchPromises);
      updateImages(fetchedImages);
      setLoadingBatch(false);
    },
    [bbox, updateImages, showClouds]
  );

  useEffect(() => {
    if (useGlobalImageLoader) {
      return;
    }
    const imagesToFetch = images.filter((img) => img.loading).slice(0, BATCH_SIZE);
    if (imagesToFetch.length > 0 && !loadingBatch) {
      fetchImageBatch(imagesToFetch);
    }
  }, [images, fetchImageBatch, loadingBatch, useGlobalImageLoader]);

  const groupImagesByMonth = () => {
    const groupedImages = {};
    // Hide scenes that came back blank. A date can exist in the archive while
    // the pass holds no pixels over this bbox; the map timeline has always
    // dropped those (App.js), the list used to render them as black squares.
    // Images still loading have no verdict yet, so they stay.
    images.filter((image) => image.loading || !isImageBlack(image)).forEach((image) => {
      const date = new Date(image.date);
      const monthYear = `${monthNames[date.getMonth()]} ${date.getFullYear()}`;
      if (!groupedImages[monthYear]) {
        groupedImages[monthYear] = [];
      }
      groupedImages[monthYear].push(image);
    });

    // Sort months by descending order (latest month first)
    const sortedMonths = Object.keys(groupedImages).sort(
      (a, b) => new Date(b) - new Date(a)
    );

    // Sort dates within each month in ascending order
    sortedMonths.forEach((monthYear) => {
      groupedImages[monthYear].sort((a, b) => new Date(a.date) - new Date(b.date));
    });

    return groupedImages;
  };

  const groupedImages = groupImagesByMonth();

  const loadMore = useCallback(() => {
    const newYear = year - 1;
    setYear(newYear);
    fetchImagesForYear(newYear);
  }, [year, fetchImagesForYear]);

  const handleImageClick = (image) => {
    if (image.loading || !image.url) {
      // Prevent clicking if the image is still loading or URL is not available
      return;
    }
    onImageClick(image);
  };
  
  // Removed unused handleToggleFalseColor function

  return (
    <div className="image-list">
      {Object.entries(groupedImages).map(([monthYear, monthImages]) => (
        <div key={monthYear} className="month-section">
          <h3>{monthYear}</h3>
          <TransitionGroup className="image-grid">
            {monthImages.map((image) => (
              <CSSTransition
                key={`image-${image.date}-${image.path}`}
                timeout={500}
                classNames="fade"
              >
                <ImageCell
                  image={image}
                  onImageClick={handleImageClick}
                  showClouds={showClouds}
                  boxDimensions={boxDimensions}
                  imageViewMode={imageViewMode}
                  isSelected={selectedImagePaths.has(image.path)}
                />
              </CSSTransition>
            ))}
            {Array.from({ length: 6 }, (_, i) => (
              <div
                key={`spacer-${i}`}
                className="image-grid-spacer"
                style={{ width: boxDimensions ? boxDimensions.width : 90 }}
              />
            ))}
          </TransitionGroup>
        </div>
      ))}
      <button
        className="load-more-button"
        onClick={loadMore}
        disabled={loading || effectiveLoadingBatch}
      >
        {effectiveLoadingBatch ? <div className="spinner"></div> : 'Load more'}
      </button>
    </div>
  );
};

export default ImageList;
